import { Component, inject, Input, OnInit, OnChanges, SimpleChanges, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToggleBtnsComponent } from '../../../../../shared/components/toggle-btns/toggle-btns.component';
import { ScrollTableComponent, TableHeader } from '../../../../../shared/components/tables/scrolled-table/scroll-table.component';
import { LineChartComponent } from '../../../../../shared/components/charts/line-chart/line-chart.component';
import { SelectionComponent, SelectionOption } from '../../../../../shared/components/ui/selection/selection.component';
import { 
  businessStages, 
  businessPhases, 
  businessStageData,
  businessPhasesData
} from '../../../../../shared/utils/dummyData';
import { DateRangePickerOutput } from '../../../../../shared/components/date-picker/date-picker.component';
import { ApiService } from '../../../../../core/services/api.service';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import { IndividualToasterComponent } from "../../../../../shared/components/loader/loader.component";

dayjs.extend(quarterOfYear);

interface ToggleOption {
  label: string;
  icon?: string;
  tooltip?: string;
  isTable?: boolean;
}

@Component({
  standalone: true,
  selector: 'app-business-stage-phase-stats',
  imports: [CommonModule, FormsModule, ToggleBtnsComponent, ScrollTableComponent, LineChartComponent, SelectionComponent, IndividualToasterComponent],
  templateUrl: './business-stage-phase-stats.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './business-stage-phase-stats.component.scss'
})
export class BusinessStagePhaseStatsComponent implements OnInit, OnChanges {
  @Input() dataType: 'stage' | 'phase' = 'stage'; // Parent can specify which data to show
  @Input() dateRange!: DateRangePickerOutput;
  @Input() productServerName: string = 'denefits';
  @Input() requiresAccessToken: boolean = false;
  @Input() cocuId: number = 1;
  @Input() countryId: number = 1;
  isLoading = signal(false)
  // Toggle options for Graph/Table view
  toggleOptions: ToggleOption[] = [
    {
      label: 'Graph',
      icon: 'assets/img/PNG/Vector.png',
      tooltip: 'Click to View Graph',
      isTable: false,
    },
    {
      label: 'Table',
      icon: 'assets/img/PNG/show_table.png',
      tooltip: 'Click to View Table',
      isTable: true,
    },
  ];

  private viewSignal: WritableSignal<'chart' | 'table'> = signal('table');

  // Available stages and phases from dummy data
  businessStages = businessStages;
  businessPhases = businessPhases;

  // Current selected stage/phase
  selectedStageId: number = 1; // Default to "Registered"
  selectedPhaseId: string = '1'; // Default to "Phase 0"

  // Data for display
  currentData: any = null;
  previousData: any = null;
  chartData: any[] = [];
  previousChartData: any[] = [];
  tableData: any[] = [];
  previousTableData: any[] = [];
  
  // Stats
  currentTotal: number = 0;
  previousTotal: number = 0;
  percentChange: number = 0;

  // Table headers - will be dynamically generated
  tableHeaders: TableHeader[] = [];

  constructor(private apiService: ApiService) { }

  ngOnInit(): void {
    this.loadData();
    this.updateTableHeaders();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['dateRange'] && changes['dateRange'].currentValue) || 
        (changes['productServerName'] && !changes['productServerName'].firstChange) ||
        (changes['cocuId'] && !changes['cocuId'].firstChange) ||
        (changes['countryId'] && !changes['countryId'].firstChange)) {
      // Update table headers when dateRange changes
      this.updateTableHeaders();
      
      // When dateRange or ID changes, reload data
      if (this.dateRange?.previous_start_date && this.dateRange?.previous_end_date) {
        // Fetch both current and previous period data
        this.fetchData(false, true, false);
        this.fetchData(false, true, true);
      } else {
        // Fetch only current period data
        this.fetchData();
      }
    }
  }

  get view() {
    return this.viewSignal();
  }

  get title(): string {
    return this.dataType === 'stage' ? 'BUSINESS STAGE' : 'BUSINESS PHASE';
  }

  get selectedLabel(): string {
    if (this.dataType === 'stage') {
      const stage = this.businessStages.find(s => s.stage === this.selectedStageId);
      return stage?.name || '';
    } else {
      const phase = this.businessPhases.find(p => p.phase_ids === this.selectedPhaseId);
      return phase?.phase_name || '';
    }
  }

  get selectorOptions(): SelectionOption<number | string>[] {
    if (this.dataType === 'stage') {
      return this.businessStages.map(stage => ({
        value: stage.stage,
        label: stage.name
      }));
    } else {
      return this.businessPhases.map(phase => ({
        value: phase.phase_ids,
        label: phase.phase_name
      }));
    }
  }

  get selectedValue(): number | string {
    return this.dataType === 'stage' ? this.selectedStageId : this.selectedPhaseId;
  }

  loadData(): void {
    // Use dummy data as fallback, but prefer API data when available
    const data = this.dataType === 'stage' ? businessStageData : businessPhasesData;
    const unit = this.dateRange?.unit || 'day';

    this.currentTotal = data.stats.total;
    this.previousTotal = 0; // Would come from previous period data
    this.percentChange = this.previousTotal > 0 
      ? ((this.currentTotal - this.previousTotal) / this.previousTotal) * 100 
      : 0;

    // Build chart data
    this.chartData = data.records.map(item => ({
      start: item.start,
      end: item.end,
      value: item.total,
      key: 'Business' + this.dataType === 'stage' ? ' Stage' : ' Phase'
    }));

    // Build table data with formatted dates
    this.tableData = data.records.map(item => ({
      start: this.formatStartDate(item.start, item.end, unit),
      total: item.total,
      dental: item.dental,
      healthcare: item.healthcare,
      non_healthcare: item.non_healthcare,
    }));

    // For demo, we'll create mock previous data (in real scenario this would come from API)
    this.previousChartData = [];
    this.previousTableData = [];
  }

  updateTableHeaders(): void {
    const unit = this.dateRange?.unit || 'day';
    const dateLabel = this.getDateLabel(unit);
    
    // Format date ranges for current and previous periods using unit-aware formatting
    const currentPeriodLabel = this.dateRange?.start_date && this.dateRange?.end_date
      ? this.formatDateRangeByUnit(this.dateRange.start_date, this.dateRange.end_date, unit)
      : '';
    
    const previousPeriodLabel = this.dateRange?.previous_start_date && this.dateRange?.previous_end_date
      ? this.formatDateRangeByUnit(this.dateRange.previous_start_date, this.dateRange.previous_end_date, unit)
      : '';

    this.tableHeaders = [
      { label: dateLabel, key: 'start', align: 'text-left', noTransform: true },
      { label: `Current${currentPeriodLabel ? ` (${currentPeriodLabel})` : ''}`, key: 'total', align: 'text-left', showProgress: true },
      { label: `Previous${previousPeriodLabel ? ` (${previousPeriodLabel})` : ''}`, key: 'total', align: 'text-left', isPrevious: true },
    ];
  }

  private getDateLabel(unit: string): string {
    const labels: { [key: string]: string } = {
      'hour': 'Hour',
      'day': 'Date',
      'week': 'Week',
      'month': 'Month',
      'quarter': 'Quarter',
      'year': 'Year'
    };
    return labels[unit] || 'Date';
  }

  private formatStartDate(startDate: string, endDate: string, unit: string): string {
    if (unit === 'week') {
      return `${dayjs(startDate).format('DD MMM')} - ${dayjs(endDate).format('DD MMM')}`;
    } else if (unit === 'day') {
      return `${dayjs(startDate).format('DD MMM')}`;
    } else if (unit === 'quarter') {
      return `${dayjs(startDate).format('MMM YY')} - ${dayjs(endDate).format('MMM YY')}`;
    } else if (unit === 'year') {
      return `${dayjs(startDate).format('YYYY')}`;
    } else if (unit === 'month') {
      return `${dayjs(startDate).format('MMM YYYY')}`;
    } else if (unit === 'hour') {
      return `${dayjs(startDate).format('DD MMM HH:mm')}`;
    }
    return dayjs(startDate).format('DD MMM');
  }

  private formatDateRangeByUnit(startDate: string, endDate: string, unit: string): string {
    const start = dayjs(startDate);
    const end = dayjs(endDate);

    switch (unit) {
      case 'hour':
        // For hourly: show date with hour range
        if (start.isSame(end, 'day')) {
          return `${start.format('DD MMM')} (${start.format('HH:mm')} - ${end.format('HH:mm')})`;
        }
        return `${start.format('DD MMM HH:mm')} - ${end.format('DD MMM HH:mm')}`;
      
      case 'day':
        // For daily: show date range
        if (start.isSame(end, 'month')) {
          return `${start.format('DD')} - ${end.format('DD MMM YYYY')}`;
        }
        return `${start.format('DD MMM')} - ${end.format('DD MMM YYYY')}`;
      
      case 'week':
        // For weekly: show date range
        return `${start.format('DD MMM')} - ${end.format('DD MMM YYYY')}`;
      
      case 'month':
        // For monthly: show month range
        if (start.isSame(end, 'year')) {
          return `${start.format('MMM')} - ${end.format('MMM YYYY')}`;
        }
        return `${start.format('MMM YYYY')} - ${end.format('MMM YYYY')}`;
      
      case 'quarter':
        // For quarterly: show quarter range
        return `Q${start.quarter()} ${start.format('YYYY')} - Q${end.quarter()} ${end.format('YYYY')}`;
      
      case 'year':
        // For yearly: show year range
        if (start.year() === end.year()) {
          return start.format('YYYY');
        }
        return `${start.format('YYYY')} - ${end.format('YYYY')}`;
      
      default:
        return `${start.format('MMM YYYY')} - ${end.format('MMM YYYY')}`;
    }
  }

  private fetchData(
    useCache: boolean = false,
    useLoader: boolean = false,
    isPrevious: boolean = false
  ) {
    if (!this.dateRange) {
      console.warn('DateRange not available yet');
      return;
    }
this.isLoading.set(true)
    // Determine the API endpoint based on dataType
    const apiType = this.dataType === 'stage' ? 'business_stage_data' : 'business_phase_data';
    
    const payload: any = {
      cocu_id: this.cocuId,
      country_id: this.countryId,
      start_time: isPrevious
        ? this.dateRange.previous_start_date
        : this.dateRange.start_date || new Date(),
      end_time: isPrevious
        ? this.dateRange.previous_end_date
        : this.dateRange.end_date || new Date(),
      is_dummy: 0,
      // timezone: 'PDT',
      unit: this.dateRange?.unit || 'day',
      outputFormat: 'db',
      // Include stage or phase filter based on selection
      ...(this.dataType === 'stage' 
        ? { stage: this.selectedStageId }
        : { phase_ids: this.selectedPhaseId }
      ),
    };

    if (this.productServerName === 'credee') {
      payload['timezone'] = 'PDT';
    }

    this.apiService
      .post(`/visionboard/stats/${apiType}`, payload, {
        addAccessToken: this.requiresAccessToken,
        productServerName: this.productServerName,
        showErrorToaster: true,
        showSuccessToaster: false,
        showLoader: false,
      })
      .subscribe({
        next: (response: any) => {
          if (response?.is_error === 0 && response?.data) {
            this.isLoading.set(false)
            
            
            const unit = this.dateRange?.unit || 'day';
            
            // Transform records to chart format
            const chartData = (response.data.records || []).map((item: any) => ({
              start: item.start,
              end: item.end,
              value: item.total,
              key: this.selectedLabel
            }));

            // Transform records to table format with formatted dates
            const tableData = (response.data.records || []).map((item: any) => ({
              start: this.formatStartDate(item.start, item.end, unit),
              total: item.total,
              dental: item.dental,
              healthcare: item.healthcare,
              non_healthcare: item.non_healthcare,
            }));

            // Update data based on whether this is previous period data
            if (isPrevious) {
              // Update previous period data
              this.previousChartData = (chartData || []).map((item: any) => ({
                ...item,
                key: 'Previous' + ' ' + this.selectedLabel
              }));
              this.previousTableData = tableData;
              this.previousTotal = response.data.stats?.total || 0;
              
              // Recalculate percent change
              if (this.previousTotal > 0) {
                this.percentChange = ((this.currentTotal - this.previousTotal) / this.previousTotal) * 100;
              }
            } else {
              this.isLoading.set(false)
              // Update current period data
              this.chartData = (chartData || []).map((item: any) => ({  
                ...item,
                key: 'Current' + ' ' +  this.selectedLabel
              }));
              this.tableData = tableData;
              this.currentTotal = response.data.stats?.total || 0;
              
              // Clear previous data if comparison period is not selected
              if (!(this.dateRange.previous_start_date && this.dateRange.previous_end_date)) {
                this.previousChartData = [];
                this.previousTableData = [];
                this.previousTotal = 0;
                this.percentChange = 0;
              }
            }
          }
        },
        error: (error: any) => {
          console.error(`Error fetching ${apiType}:`, error);
          this.isLoading.set(false)
        }
      });
  }

  onToggle(event: any): void {
    if (event === 'table' || event === 'graph') {
      this.viewSignal.set(event === 'table' ? 'table' : 'chart');
      return;
    }

    let value: boolean | undefined;
    if (typeof event === 'boolean') value = event;
    else if (event && typeof event === 'object') {
      value = (event.value ?? event.detail ?? event.payload ?? event.checked) as any;
      if (typeof value !== 'boolean') {
        if ('isTable' in event) value = !!event.isTable;
        else value = event === 'table' ? true : undefined;
      }
    }

    this.viewSignal.set(value ? 'table' : 'chart');
  }

  onStagePhaseChange(value: number | string): void {
    if (this.dataType === 'stage') {
      this.selectedStageId = value as number;
    } else {
      this.selectedPhaseId = value as string;
    }
    
    // Reload data when stage/phase selection changes
    if (this.dateRange) {
      if (this.dateRange?.previous_start_date && this.dateRange?.previous_end_date) {
        this.fetchData(false, true, false);
        this.fetchData(false, true, true);
      } else {
        this.fetchData();
      }
    } else {
      // Fallback to dummy data if dateRange not available
      this.loadData();
    }
  }

  onSelectionChange(value: number | string | (number | string)[]): void {
    // Handle single selection (array should never come through in single mode, but type-safe)
    const singleValue = Array.isArray(value) ? value[0] : value;
    if (singleValue !== undefined) {
      this.onStagePhaseChange(singleValue);
    }
  }

}
